import SwapVertIcon from "@mui/icons-material/SwapVertRounded";
import { Stack, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import type { Grouping } from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import PopoverMenuItem from "@entrilia/shared/components/PopoverMenuItem.tsx";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch.ts";
import { selectStatementDataColumns } from "../../../../store/selectors/fieldsStateSelectors.ts";
import type { StatementAxisState } from "../../../../store/types/fieldsState.types.ts";
import { createFinancialStatementAction } from "../../../../store/utils/storeUtils.ts";
import FieldOptionDeleteButton from "../../../viewer/common/fields/FieldOptionDeleteButton.tsx";
import FieldOptionsDivider from "../../../viewer/common/fields/FieldOptionsDivider.tsx";
import FieldPopover from "../../../viewer/common/fields/FieldPopover.tsx";
import SwitchMenuItem from "../../common/fields/field-options/SwitchMenuItem.tsx";
import { isReferenceDateField } from "../../common/utilities/isReferenceDateField.ts";
import DateGroupingComponent from "../../tabular/table/DateGroupingComponent.tsx";
import { axisDateGroupings, getGroupingOffsetConflict } from "../utils/statementColumns.ts";

interface Props {
  axis: StatementAxisState;
  canMakeOuter: boolean;
  anchorEl: HTMLElement;
  readonly: boolean;
  onClose: () => void;
}

const AxisOptionsPopover = ({ axis, canMakeOuter, anchorEl, readonly, onClose }: Props) => {
  const dispatch = useAppDispatch();
  const dataColumns = useSelector(selectStatementDataColumns);

  const { field, showTotal } = axis;
  const guid = field.config.guid;

  const updateAxis = (changes: Partial<StatementAxisState>) =>
    dispatch(createFinancialStatementAction("updateAxis", { guid, changes }));

  const handleGroupingChange = (grouping: Grouping | null) => {
    if (grouping && grouping !== field.config.grouping) {
      updateAxis({ field: { ...field, config: { ...field.config, grouping } } });
    }
  };

  const handleMakeOuter = () => {
    dispatch(createFinancialStatementAction("makeOuterAxis", guid));
    onClose();
  };

  const handleRemove = () => {
    dispatch(createFinancialStatementAction("removeAxis", guid));
    onClose();
  };

  return (
    <FieldPopover open anchorEl={anchorEl} onClose={onClose} sx={{ width: 400, p: 0 }}>
      <Stack sx={{ px: 2, pt: 2, pb: 1 }}>
        <Typography variant="caption" color="textSecondary">
          Column Axis
        </Typography>
        <Typography noWrap>{field.meta.caption}</Typography>
      </Stack>
      <FieldOptionsDivider />
      {canMakeOuter && !readonly && (
        <>
          <PopoverMenuItem sx={{ gap: 1 }} onClick={handleMakeOuter}>
            <SwapVertIcon color="secondary" />
            <Typography>Make Outer Axis</Typography>
          </PopoverMenuItem>
          <FieldOptionsDivider />
        </>
      )}
      {isReferenceDateField(field.meta) && (
        <>
          <DateGroupingComponent
            field={field}
            supportedDateGroupings={axisDateGroupings}
            updateGrouping={handleGroupingChange}
            getGroupingDisabledReason={(grouping) => getGroupingOffsetConflict(grouping, dataColumns)}
            readonly={readonly}
          />
          <FieldOptionsDivider />
        </>
      )}
      <SwitchMenuItem
        caption="Show Total Column"
        value={showTotal}
        onClick={() => updateAxis({ showTotal: !showTotal })}
        disabled={readonly}
      />
      <FieldOptionsDivider />
      {!readonly && <FieldOptionDeleteButton onRemove={handleRemove}>Remove Axis</FieldOptionDeleteButton>}
    </FieldPopover>
  );
};

export default AxisOptionsPopover;
